import { useCallback } from "react";

import type { Awaitable } from "@alaffia-technology-solutions/types";

import { Agent } from "@alaffia-technology-solutions/schema";

import { parseAsJson, useQueryState } from "nuqs";
import { z } from "zod";

import type { PdfViewerPreviewAction } from "../../files/hooks/usePDFViewerPreview";

import {
  pdfViewerPreviewActions,
  pdfViewerPreviewReducer,
  pdfViewerPreviewSchema,
} from "../../files/hooks/usePDFViewerPreview";

const agentStateSchema = z.object({
  messageId: z.string(),
  sidebarExpanded: z.boolean().default(true),
  editMode: z.boolean().default(false),
});

export type AgentStateSchema = z.infer<typeof agentStateSchema>;

const agentsStateSchema = z.record(z.string(), agentStateSchema);

const chatboxStateSchema = z.object({
  sidebarExpanded: z.boolean().default(false).optional(),
});

export type ChatboxStateSchema = z.infer<typeof chatboxStateSchema>;

export const caseWorkflowSchema = z.object({
  caseId: z.string(),
  selectedTab: z.union([z.literal("chat"), Agent.shape.id]).optional(),
  pdfViewerPreview: pdfViewerPreviewSchema.optional(),
  chatbox: chatboxStateSchema.optional(),
  agents: agentsStateSchema.optional(),
});

export type CaseWorkflowSchema = z.infer<typeof caseWorkflowSchema>;

export type CaseWorkflowTab = CaseWorkflowSchema["selectedTab"];

export type CaseWorkflowState = CaseWorkflowSchema | null;

interface AgentStateChangedAction {
  type: "case-workflow-agent-state-changed";
  agentId: Agent["id"];
  agentState: AgentStateSchema;
}
interface ChatboxStateChangedAction {
  type: "case-workflow-chatbox-state-changed";
  chatboxState: ChatboxStateSchema;
}
interface SelectedTabChangedAction {
  type: "case-workflow-selected-tab-changed";
  selectedTab: CaseWorkflowTab;
}
interface CaseWorkflowClosedAction {
  type: "case-workflow-closed";
}
interface CaseWorkflowOpenedAction {
  type: "case-workflow-opened";
  caseId: string;
}
interface InitialFilesDataLoadedAction {
  type: "case-workflow-initial-files-data-loaded";
  filesData: { id: string }[];
}

export type CaseWorkflowAction =
  | AgentStateChangedAction
  | ChatboxStateChangedAction
  | SelectedTabChangedAction
  | CaseWorkflowClosedAction
  | CaseWorkflowOpenedAction
  | InitialFilesDataLoadedAction
  // Case workflow supports all PdfViewerPreview actions too
  | PdfViewerPreviewAction;

export type CaseWorkflowDispatcher = (
  action: CaseWorkflowAction,
) => Awaitable<void>;

export const caseWorkflowActions = {
  pdfViewerPreview: pdfViewerPreviewActions,

  agentStateChanged: (
    agentId: Agent["id"],
    agentState: AgentStateSchema,
  ): AgentStateChangedAction => ({
    type: "case-workflow-agent-state-changed",
    agentId,
    agentState,
  }),

  chatboxStateChanged: (
    chatboxState: ChatboxStateSchema,
  ): ChatboxStateChangedAction => ({
    type: "case-workflow-chatbox-state-changed",
    chatboxState,
  }),

  selectedTabChanged: (
    selectedTab: CaseWorkflowTab,
  ): SelectedTabChangedAction => ({
    type: "case-workflow-selected-tab-changed",
    selectedTab,
  }),

  caseWorkflowClosed: (): CaseWorkflowClosedAction => ({
    type: "case-workflow-closed",
  }),

  caseWorkflowOpened: (caseId: string): CaseWorkflowOpenedAction => ({
    type: "case-workflow-opened",
    caseId,
  }),

  initialFilesDataLoaded: (
    filesData: { id: string }[],
  ): InitialFilesDataLoadedAction => ({
    type: "case-workflow-initial-files-data-loaded",
    filesData,
  }),
};

export const caseWorkflowReducer = (
  state: CaseWorkflowState,
  action: CaseWorkflowAction,
): CaseWorkflowState => {
  switch (action.type) {
    case "case-workflow-opened":
      return {
        caseId: action.caseId,
      };

    case "case-workflow-closed":
      return null;

    case "case-workflow-initial-files-data-loaded":
      if (!state) return state;
      if (state.pdfViewerPreview) return state;

      if (action.filesData[0]?.id) {
        return {
          ...state,
          pdfViewerPreview: {
            fileId: action.filesData[0].id,
          },
        };
      }

      return {
        ...state,
      };

    case "case-workflow-agent-state-changed":
      if (!state) return state;

      return {
        ...state,
        agents: {
          ...state.agents,
          [action.agentId]: action.agentState,
        },
      };

    case "case-workflow-chatbox-state-changed":
      if (!state) return state;

      return {
        ...state,
        chatbox: action.chatboxState,
      };

    case "case-workflow-selected-tab-changed":
      if (!state) return state;

      return {
        ...state,
        selectedTab: action.selectedTab,
      };

    case "pdf-viewer-opened":
    case "pdf-viewer-search-query-changed":
    case "pdf-viewer-sidebar-tab-changed":
    case "pdf-viewer-closed":
    case "pdf-viewer-active-annotation-changed":
    case "pdf-viewer-page-changed":
    case "pdf-viewer-view-mode-changed":
      if (!state) return state;

      return {
        ...state,
        pdfViewerPreview:
          pdfViewerPreviewReducer(state.pdfViewerPreview ?? null, action) ??
          undefined,
      };

    default:
      action satisfies never;
      return state;
  }
};

export function useCaseWorkflowDialog() {
  const [caseWorkflow, setCaseWorkflowDialog] = useQueryState(
    "caseWorkflow",
    // eslint-disable-next-line @typescript-eslint/unbound-method
    parseAsJson(caseWorkflowSchema.parse),
  );

  const dispatchCaseWorkflowAction = useCallback(
    async (action: CaseWorkflowAction) => {
      await setCaseWorkflowDialog((value) =>
        caseWorkflowReducer(value, action),
      );
    },
    [setCaseWorkflowDialog],
  );

  return {
    caseWorkflow,
    dispatchCaseWorkflowAction,
  };
}
